import type {
  Bid,
  BidLeveling,
  BidPackage,
  BidSolicitation,
  ChangeOrder,
  ClientCommunication,
  ComplianceItem,
  CreateLeadResult,
  CreateTouchpointDirectionApi,
  CreateTouchpointTypeApi,
  DailyLog,
  DashboardSummary,
  Decision,
  DigestResult,
  Draw,
  DrawDraft,
  InboundEmail,
  InboundInvoice,
  InboundSummary,
  Inspection,
  Lead,
  LeadDetailResponse,
  LeadListResponse,
  LeadTouchpoint,
  Lesson,
  LotMonitoring,
  Material,
  Me,
  PlanSheet,
  PlanVersion,
  Project,
  PunchItem,
  ScheduleTask,
  ScopeGap,
  ScopeItem,
  ScsItem,
  Selection,
  SiteWalk,
  SiteWalkItem,
  SpecComplianceFlag,
  WarrantyClaim,
  SpecSection,
  SpecSubsection,
  SpecVersion,
  Sub,
  SubAgreement,
  SubNotification,
} from '../types'

// Empty string in prod = same-origin (Worker route at /api/*).
// Localhost fallback for dev when VITE_API_URL is unset.
const envUrl = import.meta.env.VITE_API_URL as string | undefined
const API_URL: string = envUrl !== undefined ? envUrl : 'http://localhost:8787'

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  })
  const data = (await res.json()) as { ok: boolean; error?: string } & T
  if (!data.ok) {
    throw new Error(data.error ?? `HTTP ${res.status}`)
  }
  return data
}

// Generic helpers for routes that follow the standard list/single pattern
const get = <T>(path: string) => req<T>(path)
const post = <T>(path: string, body: unknown) =>
  req<T>(path, { method: 'POST', body: JSON.stringify(body) })
const put = <T>(path: string, body: unknown) =>
  req<T>(path, { method: 'PUT', body: JSON.stringify(body) })
const patch = <T>(path: string, body: unknown) =>
  req<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
const del = <T>(path: string) => req<T>(path, { method: 'DELETE' })

export const api = {
  apiUrl: API_URL,

  health: () => get<{ ok: true; db: boolean; projects: number; environment: string }>('/api/health'),

  me: () => get<{ ok: true } & Me>('/api/me'),

  weeklyDigest: (projectId: string) =>
    post<{ ok: true } & DigestResult>(`/api/projects/${projectId}/weekly-digest`, {}),

  dashboard: () => get<{ ok: true } & DashboardSummary>('/api/dashboard'),

  integrations: {
    status: () =>
      get<{
        ok: true
        qbo: {
          configured: boolean
          connected: boolean
          env: 'sandbox' | 'production'
          realm_id: string | null
          connected_at: string | null
          last_synced_at: string | null
          last_error: string | null
        }
        gmail: {
          configured: boolean
          connected: boolean
          account_email: string | null
          connected_at: string | null
          last_error: string | null
          last_polled_at: string | null
          last_message_count: number | null
          last_poll_error: string | null
        }
      }>('/api/integrations'),
    qboConnectUrl: () => `${API_URL}/api/integrations/qbo/connect`,
    qboDisconnect: () => post<unknown>('/api/integrations/qbo/disconnect', {}),
    qboSync: () => post<{ fetched: number; since: string }>('/api/integrations/qbo/sync', {}),
    gmailConnectUrl: () => `${API_URL}/api/integrations/gmail/connect`,
    gmailDisconnect: () => post<unknown>('/api/integrations/gmail/disconnect', {}),
    gmailSend: (body: { to: string; subject: string; body: string }) =>
      post<{ message_id: string }>('/api/integrations/gmail/send', body),
  },

  inbound: {
    list: (params: {
      status?: string
      document_type?: string
      project_id?: string
      limit?: number
    } = {}) => {
      const q = new URLSearchParams()
      if (params.status) q.set('status', params.status)
      if (params.document_type) q.set('document_type', params.document_type)
      if (params.project_id) q.set('project_id', params.project_id)
      if (params.limit) q.set('limit', String(params.limit))
      const qs = q.toString()
      return get<{ emails: InboundEmail[]; summary: InboundSummary }>(
        `/api/inbound${qs ? `?${qs}` : ''}`
      )
    },
    get: (id: string) =>
      get<{ email: InboundEmail; invoice: InboundInvoice | null }>(`/api/inbound/${id}`),
    update: (id: string, body: Partial<InboundEmail>) =>
      put<{ email: InboundEmail }>(`/api/inbound/${id}`, body).then((d) => d.email),
    ignore: (id: string) => del<unknown>(`/api/inbound/${id}`),
    poll: () =>
      post<{
        fetched: number
        inserted: number
        skipped_duplicates: number
        ai_processed: number
        ai_errors: number
        errors: Array<{ gmail_message_id: string; message: string }>
      }>('/api/inbound/poll', {}),
    reprocess: (id: string) =>
      post<{ extraction: unknown }>(`/api/inbound/${id}/reprocess`, {}),
    invoices: {
      list: (params: { project_id?: string; draw_status?: string } = {}) => {
        const q = new URLSearchParams()
        if (params.project_id) q.set('project_id', params.project_id)
        if (params.draw_status) q.set('draw_status', params.draw_status)
        const qs = q.toString()
        return get<{ invoices: InboundInvoice[] }>(
          `/api/inbound-invoices${qs ? `?${qs}` : ''}`
        ).then((d) => d.invoices)
      },
      update: (id: string, body: Partial<InboundInvoice>) =>
        put<{ invoice: InboundInvoice }>(`/api/inbound-invoices/${id}`, body).then(
          (d) => d.invoice
        ),
    },
  },

  insights: (projectId: string) =>
    post<{
      summary: string
      patterns: Array<{ title: string; evidence: string; recommendation: string }>
      data_points: Record<string, number>
    }>(`/api/projects/${projectId}/insights`, {}),

  financials: (projectId: string) =>
    get<{
      ok: true
      project: { id: string; name: string }
      budget: {
        original: number
        with_approved_cos: number
        drawn_to_date: number
        remaining: number
      }
      change_orders: { total: number; approved_impact: number; pending_impact: number }
      draws: { total: number; funded_total: number }
      heritage_revenue: { gc_fee_total: number; gc_fee_received: number; gc_fee_remaining: number }
      qbo_actuals: { tracked_total: number }
    }>(`/api/projects/${projectId}/financials`),

  compliance: {
    list: () => get<{ items: ComplianceItem[] }>('/api/compliance').then((d) => d.items),
    create: (body: Partial<ComplianceItem>) =>
      post<{ item: ComplianceItem }>('/api/compliance', body).then((d) => d.item),
    update: (id: string, body: Partial<ComplianceItem>) =>
      put<{ item: ComplianceItem }>(`/api/compliance/${id}`, body).then((d) => d.item),
    delete: (id: string) => del<unknown>(`/api/compliance/${id}`),
  },

  bids: {
    list: (projectId: string) =>
      get<{ bids: Bid[] }>(`/api/projects/${projectId}/bids`).then((d) => d.bids),
    create: (projectId: string, body: Partial<Bid>) =>
      post<{ bid: Bid }>(`/api/projects/${projectId}/bids`, body).then((d) => d.bid),
    update: (id: string, body: Partial<Bid>) =>
      put<{ bid: Bid }>(`/api/bids/${id}`, body).then((d) => d.bid),
    delete: (id: string) => del<unknown>(`/api/bids/${id}`),
  },

  lessons: {
    listAll: () => get<{ lessons: Lesson[] }>('/api/lessons').then((d) => d.lessons),
    list: (projectId: string) =>
      get<{ lessons: Lesson[] }>(`/api/projects/${projectId}/lessons`).then((d) => d.lessons),
    create: (projectId: string, body: Partial<Lesson>) =>
      post<{ lesson: Lesson }>(`/api/projects/${projectId}/lessons`, body).then((d) => d.lesson),
    update: (id: string, body: Partial<Lesson>) =>
      put<{ lesson: Lesson }>(`/api/lessons/${id}`, body).then((d) => d.lesson),
    delete: (id: string) => del<unknown>(`/api/lessons/${id}`),
  },

  // Projects
  projects: {
    list: () => get<{ projects: Project[] }>('/api/projects').then((d) => d.projects),
    get: (id: string) => get<{ project: Project }>(`/api/projects/${id}`).then((d) => d.project),
    create: (body: Partial<Project>) =>
      post<{ project: Project }>('/api/projects', body).then((d) => d.project),
    update: (id: string, body: Partial<Project>) =>
      put<{ project: Project }>(`/api/projects/${id}`, body).then((d) => d.project),
    export: (id: string) => fetch(`${API_URL}/api/projects/${id}/export`),
    readinessRollup: (id: string) =>
      get<{
        ok: true
        project_id: string
        synthesis: 'ready' | 'not_ready' | 'run'
        groundbreak_alert: {
          color: 'red' | 'gold'
          target_date: string
          days_to_target: number
          message: string
        } | null
        pills: {
          permits: { value: string; color: 'green' | 'gold' | 'red'; detail: unknown }
          subs_ready: { value: string; color: 'green' | 'gold' | 'red'; detail: unknown }
          long_leads: { value: number; color: 'green' | 'gold' | 'red'; detail: unknown }
          decisions: { value: number | string; color: 'green' | 'gold' | 'red' | 'gray'; detail: unknown }
          spec_compliance: { value: number; color: 'green' | 'gold' | 'red'; detail: unknown }
          weather: { value: number; color: 'green' | 'gold' | 'red'; detail: unknown }
        }
      }>(`/api/projects/${id}/readiness-rollup`),
    addClientEmail: async (projectId: string, email: string) => {
      const cur = await get<{ project: Project }>(`/api/projects/${projectId}`).then((d) => d.project)
      const list: string[] = (() => {
        try {
          return cur.client_emails ? (JSON.parse(cur.client_emails) as string[]) : []
        } catch {
          return []
        }
      })()
      const norm = email.trim().toLowerCase()
      if (!norm.includes('@')) throw new Error('not an email address')
      if (list.includes(norm)) throw new Error('email already on the list')
      const next = [...list, norm]
      return put<{ project: Project }>(`/api/projects/${projectId}`, {
        client_emails: JSON.stringify(next),
      }).then((d) => d.project)
    },
    removeClientEmail: async (projectId: string, email: string) => {
      const cur = await get<{ project: Project }>(`/api/projects/${projectId}`).then((d) => d.project)
      const list: string[] = (() => {
        try {
          return cur.client_emails ? (JSON.parse(cur.client_emails) as string[]) : []
        } catch {
          return []
        }
      })()
      const norm = email.trim().toLowerCase()
      const next = list.filter((e) => e.toLowerCase() !== norm)
      return put<{ project: Project }>(`/api/projects/${projectId}`, {
        client_emails: next.length ? JSON.stringify(next) : null,
      }).then((d) => d.project)
    },
  },

  // Schedule
  schedule: {
    list: (projectId: string) =>
      get<{ tasks: ScheduleTask[] }>(`/api/projects/${projectId}/schedule`).then((d) => d.tasks),
    update: (id: string, body: Partial<ScheduleTask>) =>
      put<{ task: ScheduleTask }>(`/api/schedule-tasks/${id}`, body).then((d) => d.task),
  },

  // Decisions
  decisions: {
    list: (projectId: string) =>
      get<{ decisions: Decision[] }>(`/api/projects/${projectId}/decisions`).then(
        (d) => d.decisions
      ),
    update: (id: string, body: Partial<Decision>) =>
      put<{ decision: Decision }>(`/api/decisions/${id}`, body).then((d) => d.decision),
  },

  // SCS
  scs: {
    list: (projectId: string) =>
      get<{ scs_items: ScsItem[] }>(`/api/projects/${projectId}/scs`).then((d) => d.scs_items),
    update: (id: string, body: Partial<ScsItem>) =>
      put<{ scs_item: ScsItem }>(`/api/scs-items/${id}`, body).then((d) => d.scs_item),
  },

  // Change Orders
  changeOrders: {
    list: (projectId: string) =>
      get<{ change_orders: ChangeOrder[] }>(`/api/projects/${projectId}/change-orders`).then(
        (d) => d.change_orders
      ),
    create: (projectId: string, body: Partial<ChangeOrder>) =>
      post<{ change_order: ChangeOrder }>(`/api/projects/${projectId}/change-orders`, body).then(
        (d) => d.change_order
      ),
    update: (id: string, body: Partial<ChangeOrder>) =>
      put<{ change_order: ChangeOrder }>(`/api/change-orders/${id}`, body).then(
        (d) => d.change_order
      ),
    delete: (id: string) => del<unknown>(`/api/change-orders/${id}`),
  },

  // Daily Logs
  dailyLogs: {
    list: (projectId: string) =>
      get<{ logs: DailyLog[] }>(`/api/projects/${projectId}/daily-logs`).then((d) => d.logs),
    create: (projectId: string, body: Partial<DailyLog>) =>
      post<{ log: DailyLog }>(`/api/projects/${projectId}/daily-logs`, body).then((d) => d.log),
    delete: (id: string) => del<unknown>(`/api/daily-logs/${id}`),
  },

  // Inspections
  inspections: {
    list: (projectId: string) =>
      get<{ inspections: Inspection[] }>(`/api/projects/${projectId}/inspections`).then(
        (d) => d.inspections
      ),
    create: (projectId: string, body: Partial<Inspection>) =>
      post<{ inspection: Inspection }>(`/api/projects/${projectId}/inspections`, body).then(
        (d) => d.inspection
      ),
    update: (id: string, body: Partial<Inspection>) =>
      put<{ inspection: Inspection }>(`/api/inspections/${id}`, body).then((d) => d.inspection),
    delete: (id: string) => del<unknown>(`/api/inspections/${id}`),
  },

  // Materials
  materials: {
    list: (projectId: string) =>
      get<{ materials: Material[] }>(`/api/projects/${projectId}/materials`).then(
        (d) => d.materials
      ),
    create: (projectId: string, body: Partial<Material>) =>
      post<{ material: Material }>(`/api/projects/${projectId}/materials`, body).then(
        (d) => d.material
      ),
    update: (id: string, body: Partial<Material>) =>
      put<{ material: Material }>(`/api/materials/${id}`, body).then((d) => d.material),
    delete: (id: string) => del<unknown>(`/api/materials/${id}`),
  },

  // Draws
  draws: {
    list: (projectId: string) =>
      get<{ draws: Draw[] }>(`/api/projects/${projectId}/draws`).then((d) => d.draws),
    create: (projectId: string, body: Partial<Draw>) =>
      post<{ draw: Draw }>(`/api/projects/${projectId}/draws`, body).then((d) => d.draw),
    update: (id: string, body: Partial<Draw>) =>
      put<{ draw: Draw }>(`/api/draws/${id}`, body).then((d) => d.draw),
    delete: (id: string) => del<unknown>(`/api/draws/${id}`),
    // v2 Module 4: automation
    queue: (projectId: string) =>
      get<{ queue: InboundInvoice[] }>(`/api/projects/${projectId}/draw-queue`).then((d) => d.queue),
    draftPreview: (projectId: string) =>
      get<{ draft: DrawDraft; project: { name: string; address: string | null } }>(
        `/api/projects/${projectId}/draws/draft`
      ),
    commitDraft: (projectId: string) =>
      post<{ draw: Draw; draft: DrawDraft }>(`/api/projects/${projectId}/draws/draft`, {}),
    submit: (id: string, body: { recipient_email?: string; cc?: string } = {}) =>
      post<{
        draw_id: string
        message_id: string
        recipient: string
        attachments_sent: number
      }>(`/api/draws/${id}/submit`, body),
    markFunded: (id: string, body: { funded_date?: string; notes?: string } = {}) =>
      post<{ draw: Draw; scs_lines_updated: number }>(`/api/draws/${id}/funded`, body),
  },

  // Punch list
  punch: {
    list: (projectId: string) =>
      get<{ punch_items: PunchItem[] }>(`/api/projects/${projectId}/punch-list`).then(
        (d) => d.punch_items
      ),
    create: (projectId: string, body: Partial<PunchItem>) =>
      post<{ punch_item: PunchItem }>(`/api/projects/${projectId}/punch-list`, body).then(
        (d) => d.punch_item
      ),
    update: (id: string, body: Partial<PunchItem>) =>
      put<{ punch_item: PunchItem }>(`/api/punch-items/${id}`, body).then((d) => d.punch_item),
    delete: (id: string) => del<unknown>(`/api/punch-items/${id}`),
  },

  // Subs
  subs: {
    list: () => get<{ subs: Sub[] }>('/api/subs').then((d) => d.subs),
    get: (id: string) =>
      get<{
        sub: Sub
        projects: Array<{
          id: string
          sub_id: string
          project_id: string
          project_name: string
          role: string | null
          contract_amount: number | null
          signed_date: string | null
          sub_agreement_signed: 0 | 1
        }>
        ratings: Array<{
          id: string
          sub_id: string
          project_id: string
          project_name: string
          quality_rating: number | null
          schedule_rating: number | null
          communication_rating: number | null
          cleanup_rating: number | null
          would_hire_again: 'yes' | 'no' | 'conditional' | null
          notes: string | null
          rated_at: string
        }>
      }>(`/api/subs/${id}`),
    create: (body: Partial<Sub>) => post<{ sub: Sub }>('/api/subs', body).then((d) => d.sub),
    update: (id: string, body: Partial<Sub>) =>
      put<{ sub: Sub }>(`/api/subs/${id}`, body).then((d) => d.sub),
    addPerformance: (body: {
      sub_id: string
      project_id: string
      quality_rating?: number | null
      schedule_rating?: number | null
      communication_rating?: number | null
      cleanup_rating?: number | null
      would_hire_again?: 'yes' | 'no' | 'conditional' | null
      notes?: string | null
    }) =>
      post<{ performance: Record<string, unknown> }>('/api/sub-performance', body).then(
        (d) => d.performance
      ),
  },

  // Selections
  selections: {
    list: (projectId: string) =>
      get<{ selections: Selection[] }>(`/api/projects/${projectId}/selections`).then(
        (d) => d.selections
      ),
    create: (projectId: string, body: Partial<Selection>) =>
      post<{ selection: Selection }>(`/api/projects/${projectId}/selections`, body).then(
        (d) => d.selection
      ),
    update: (id: string, body: Partial<Selection>) =>
      put<{ selection: Selection }>(`/api/selections/${id}`, body).then((d) => d.selection),
    delete: (id: string) => del<unknown>(`/api/selections/${id}`),
  },

  // Master Scope (Phase 2)
  scope: {
    list: (projectId: string) =>
      get<{ scope_items: ScopeItem[] }>(`/api/projects/${projectId}/scope`).then(
        (d) => d.scope_items
      ),
    create: (projectId: string, body: Partial<ScopeItem>) =>
      post<{ scope_item: ScopeItem }>(`/api/projects/${projectId}/scope`, body).then(
        (d) => d.scope_item
      ),
    update: (id: string, body: Partial<ScopeItem>) =>
      put<{ scope_item: ScopeItem }>(`/api/scope-items/${id}`, body).then((d) => d.scope_item),
    delete: (id: string) => del<unknown>(`/api/scope-items/${id}`),
  },

  // R2 file URL helper — same-origin, gated by Access.
  fileUrl: (key: string) => `${API_URL}/api/files/${key}`,

  // Site Walks (Phase 2)
  siteWalks: {
    list: (projectId: string) =>
      get<{ walks: SiteWalk[] }>(`/api/projects/${projectId}/site-walks`).then((d) => d.walks),
    get: (id: string) =>
      get<{ walk: SiteWalk; items: SiteWalkItem[] }>(`/api/site-walks/${id}`),
    create: (projectId: string, body: Partial<SiteWalk>) =>
      post<{ walk: SiteWalk }>(`/api/projects/${projectId}/site-walks`, body).then((d) => d.walk),
    update: (id: string, body: Partial<SiteWalk>) =>
      put<{ walk: SiteWalk }>(`/api/site-walks/${id}`, body).then((d) => d.walk),
    delete: (id: string) => del<unknown>(`/api/site-walks/${id}`),
    addItem: (walkId: string, body: Partial<SiteWalkItem>) =>
      post<{ item: SiteWalkItem }>(`/api/site-walks/${walkId}/items`, body).then((d) => d.item),
    updateItem: (id: string, body: Partial<SiteWalkItem>) =>
      put<{ item: SiteWalkItem }>(`/api/site-walk-items/${id}`, body).then((d) => d.item),
    deleteItem: (id: string) => del<unknown>(`/api/site-walk-items/${id}`),
    templates: () => get<{ templates: Record<string, string[]> }>('/api/site-walk-templates'),
    uploadPhotos: async (walkId: string, files: File[]) => {
      const fd = new FormData()
      for (const f of files) fd.append('file', f)
      const res = await fetch(`${API_URL}/api/site-walks/${walkId}/photos`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      })
      const data = (await res.json()) as { ok: boolean; error?: string; keys?: string[]; total?: number }
      if (!data.ok) throw new Error(data.error ?? `HTTP ${res.status}`)
      return { keys: data.keys ?? [], total: data.total ?? 0 }
    },
    deletePhoto: async (walkId: string, key: string) => {
      const res = await fetch(`${API_URL}/api/site-walks/${walkId}/photos`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key }),
      })
      const data = (await res.json()) as { ok: boolean; error?: string; remaining?: number }
      if (!data.ok) throw new Error(data.error ?? `HTTP ${res.status}`)
      return { remaining: data.remaining ?? 0 }
    },
    analyze: (walkId: string) =>
      post<{
        summary: string
        flags: Array<{ severity: 'info' | 'warning' | 'critical'; item: string; note: string }>
        photos_analyzed: number
        spec_book_subsections_used: number
        scope_items_used: number
      }>(`/api/site-walks/${walkId}/analyze`, {}),
    uploadVoiceNote: async (walkId: string, blob: Blob) => {
      const res = await fetch(`${API_URL}/api/site-walks/${walkId}/voice-note`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': blob.type || 'audio/webm' },
        body: blob,
      })
      const data = (await res.json()) as { ok: boolean; error?: string; key?: string }
      if (!data.ok) throw new Error(data.error ?? `HTTP ${res.status}`)
      return data
    },
    deleteVoiceNote: (walkId: string) => del<unknown>(`/api/site-walks/${walkId}/voice-note`),
  },

  // Spec Book (Phase 2)
  specBook: {
    get: () =>
      get<{
        version: SpecVersion | null
        sections: SpecSection[]
        subsections: SpecSubsection[]
      }>('/api/spec-book'),
    updateSubsection: (id: string, body: Partial<SpecSubsection>) =>
      put<{ subsection: SpecSubsection }>(`/api/spec-subsections/${id}`, body).then(
        (d) => d.subsection
      ),
  },

  // Warranty (Phase 3)
  warranty: {
    list: (projectId: string) =>
      get<{ claims: WarrantyClaim[] }>(`/api/projects/${projectId}/warranty-claims`).then(
        (d) => d.claims
      ),
    create: (projectId: string, body: Partial<WarrantyClaim>) =>
      post<{ claim: WarrantyClaim }>(`/api/projects/${projectId}/warranty-claims`, body).then(
        (d) => d.claim
      ),
    update: (id: string, body: Partial<WarrantyClaim>) =>
      put<{ claim: WarrantyClaim }>(`/api/warranty-claims/${id}`, body).then((d) => d.claim),
    delete: (id: string) => del<unknown>(`/api/warranty-claims/${id}`),
    uploadPhotos: async (claimId: string, files: File[]) => {
      const fd = new FormData()
      for (const f of files) fd.append('file', f)
      const res = await fetch(`${API_URL}/api/warranty-claims/${claimId}/photos`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      })
      const data = (await res.json()) as { ok: boolean; error?: string; keys?: string[]; total?: number }
      if (!data.ok) throw new Error(data.error ?? `HTTP ${res.status}`)
      return { keys: data.keys ?? [], total: data.total ?? 0 }
    },
  },

  // Plan Versions (Phase 2 + v2 Module 2)
  planVersions: {
    list: (projectId: string) =>
      get<{ plan_versions: PlanVersion[] }>(`/api/projects/${projectId}/plan-versions`).then(
        (d) => d.plan_versions
      ),
    create: (projectId: string, body: Partial<PlanVersion>) =>
      post<{ plan_version: PlanVersion }>(`/api/projects/${projectId}/plan-versions`, body).then(
        (d) => d.plan_version
      ),
    update: (id: string, body: Partial<PlanVersion>) =>
      put<{ plan_version: PlanVersion }>(`/api/plan-versions/${id}`, body).then(
        (d) => d.plan_version
      ),
    delete: (id: string) => del<unknown>(`/api/plan-versions/${id}`),
    // v2 Module 2
    upload: async (projectId: string, file: File, versionLabel: string, changeSummary?: string) => {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('version_label', versionLabel)
      if (changeSummary) fd.append('change_summary', changeSummary)
      const res = await fetch(`${API_URL}/api/projects/${projectId}/plan-versions/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      })
      const data = (await res.json()) as { ok: boolean; error?: string; plan_version?: PlanVersion }
      if (!data.ok) throw new Error(data.error ?? `HTTP ${res.status}`)
      return data.plan_version!
    },
    analyze: (id: string) =>
      post<{ analysis: unknown }>(`/api/plan-versions/${id}/analyze`, {}),
    generateScope: (id: string, replaceExisting = false) =>
      post<{
        scope_items_inserted: number
        scope_gaps_inserted: number
        selections_inserted: number
      }>(`/api/plan-versions/${id}/generate-scope`, { replace_existing: replaceExisting }),
    checkCompliance: (id: string) =>
      post<{ flags_inserted: number; passed_checks: string[] }>(
        `/api/plan-versions/${id}/check-compliance`,
        {}
      ),
    listSheets: (projectId: string, planVersionId?: string) => {
      const q = planVersionId ? `?plan_version_id=${planVersionId}` : ''
      return get<{ plan_sheets: PlanSheet[] }>(
        `/api/projects/${projectId}/plan-sheets${q}`
      ).then((d) => d.plan_sheets)
    },
  },

  scopeGaps: {
    list: (projectId: string, status?: string) => {
      const q = status ? `?status=${status}` : ''
      return get<{ scope_gaps: ScopeGap[] }>(
        `/api/projects/${projectId}/scope-gaps${q}`
      ).then((d) => d.scope_gaps)
    },
    update: (id: string, body: Partial<ScopeGap>) =>
      put<{ scope_gap: ScopeGap }>(`/api/scope-gaps/${id}`, body).then((d) => d.scope_gap),
    delete: (id: string) => del<unknown>(`/api/scope-gaps/${id}`),
  },

  specCompliance: {
    list: (projectId: string, params: { status?: string; severity?: string } = {}) => {
      const q = new URLSearchParams()
      if (params.status) q.set('status', params.status)
      if (params.severity) q.set('severity', params.severity)
      const qs = q.toString()
      return get<{ flags: SpecComplianceFlag[] }>(
        `/api/projects/${projectId}/spec-compliance-flags${qs ? `?${qs}` : ''}`
      ).then((d) => d.flags)
    },
    update: (id: string, body: Partial<SpecComplianceFlag>) =>
      put<{ flag: SpecComplianceFlag }>(`/api/spec-compliance-flags/${id}`, body).then((d) => d.flag),
  },

  bidPackages: {
    generate: (projectId: string, trade: string, responseDeadline?: string) =>
      post<{ bid_package: BidPackage; package_content: unknown }>(
        `/api/projects/${projectId}/bid-packages/generate`,
        { trade, response_deadline: responseDeadline }
      ),
    list: (projectId: string) =>
      get<{ bid_packages: BidPackage[] }>(`/api/projects/${projectId}/bid-packages`).then(
        (d) => d.bid_packages
      ),
    get: (id: string) =>
      get<{
        bid_package: BidPackage
        solicitations: BidSolicitation[]
        bids: Bid[]
        leveling: BidLeveling | null
      }>(`/api/bid-packages/${id}`),
    send: (id: string, body: { sub_ids: string[]; response_deadline?: string; cover_letter?: string }) =>
      post<{ sent: number; failed: number; failures: Array<{ sub_id: string; error: string }> }>(
        `/api/bid-packages/${id}/send`,
        body
      ),
    level: (id: string) => post<{ leveling: BidLeveling; ai_analysis: unknown }>(`/api/bid-packages/${id}/level`, {}),
    award: (id: string, bidId: string) =>
      post<{ bid_package: BidPackage; sub_agreement: SubAgreement }>(
        `/api/bid-packages/${id}/award`,
        { bid_id: bidId }
      ),
    delete: (id: string) => del<unknown>(`/api/bid-packages/${id}`),
  },

  subNotifications: {
    list: (params: { status?: string; project_id?: string } = {}) => {
      const q = new URLSearchParams()
      if (params.status) q.set('status', params.status)
      if (params.project_id) q.set('project_id', params.project_id)
      const qs = q.toString()
      return get<{ notifications: SubNotification[] }>(
        `/api/sub-notifications${qs ? `?${qs}` : ''}`
      ).then((d) => d.notifications)
    },
    update: (id: string, body: Partial<SubNotification>) =>
      put<{ notification: SubNotification }>(`/api/sub-notifications/${id}`, body).then(
        (d) => d.notification
      ),
    send: (id: string) =>
      post<{ notification: SubNotification }>(`/api/sub-notifications/${id}/send`, {}),
    delete: (id: string) => del<unknown>(`/api/sub-notifications/${id}`),
    scan: () =>
      post<{
        candidates: number
        drafted: number
        auto_sent: number
        failed: number
        errors: string[]
      }>('/api/sub-notifications/scan', {}),
  },

  clientComms: {
    list: (params: { status?: string; project_id?: string } = {}) => {
      const q = new URLSearchParams()
      if (params.status) q.set('status', params.status)
      if (params.project_id) q.set('project_id', params.project_id)
      const qs = q.toString()
      return get<{ communications: ClientCommunication[] }>(
        `/api/client-comms${qs ? `?${qs}` : ''}`
      ).then((d) => d.communications)
    },
    update: (id: string, body: Partial<ClientCommunication>) =>
      put<{ communication: ClientCommunication }>(`/api/client-comms/${id}`, body).then(
        (d) => d.communication
      ),
    delete: (id: string) => del<unknown>(`/api/client-comms/${id}`),
    send: (id: string) =>
      post<{ communication: ClientCommunication }>(`/api/client-comms/${id}/send`, {}),
    scan: () =>
      post<{
        detected: number
        drafted: number
        auto_sent: number
        failed: number
        errors: string[]
      }>('/api/client-comms/scan', {}),
    prefs: (projectId: string) =>
      get<{ prefs: Array<{ id: string; comm_type: string; auto_send: 0 | 1 }> }>(
        `/api/projects/${projectId}/comm-prefs`
      ).then((d) => d.prefs),
    setPref: (projectId: string, comm_type: string, auto_send: 0 | 1) =>
      put<unknown>(`/api/projects/${projectId}/comm-prefs`, { comm_type, auto_send }),
  },

  marketingLeads: {
    list: (params: {
      stage?: string[]
      source?: string[]
      contact_state?: string[]
      city_target?: string[]
      pricing_tier_estimated?: string[]
      royal_buyer_routed?: boolean
      royal_listing_routed?: boolean
      min_days_since_created?: number
      max_days_since_created?: number
      sort_by?: string
      limit?: number
      offset?: number
    } = {}) => {
      const q = new URLSearchParams()
      if (params.stage?.length) q.set('stage', params.stage.join(','))
      if (params.source?.length) q.set('source', params.source.join(','))
      if (params.contact_state?.length) q.set('contact_state', params.contact_state.join(','))
      if (params.city_target?.length) q.set('city_target', params.city_target.join(','))
      if (params.pricing_tier_estimated?.length)
        q.set('pricing_tier_estimated', params.pricing_tier_estimated.join(','))
      if (params.royal_buyer_routed !== undefined)
        q.set('royal_buyer_routed', String(params.royal_buyer_routed))
      if (params.royal_listing_routed !== undefined)
        q.set('royal_listing_routed', String(params.royal_listing_routed))
      if (params.min_days_since_created !== undefined)
        q.set('min_days_since_created', String(params.min_days_since_created))
      if (params.max_days_since_created !== undefined)
        q.set('max_days_since_created', String(params.max_days_since_created))
      if (params.sort_by) q.set('sort_by', params.sort_by)
      if (params.limit !== undefined) q.set('limit', String(params.limit))
      if (params.offset !== undefined) q.set('offset', String(params.offset))
      const qs = q.toString()
      return get<LeadListResponse>(`/api/marketing/leads${qs ? `?${qs}` : ''}`)
    },
    get: (id: string) => get<LeadDetailResponse>(`/api/marketing/leads/${id}`),
    create: (body: {
      first_name?: string | null
      last_name?: string | null
      email?: string | null
      phone?: string | null
      source?: string
      lot_status?: string | null
      existing_home_status?: string | null
      lot_address?: string | null
      project_size_estimate?: string | null
      timeline?: string | null
      budget_range?: string | null
      message?: string | null
      city_target?: string | null
      contact_method_preference?: string | null
    }) => post<CreateLeadResult>('/api/marketing/leads', body),
    patch: (id: string, body: Partial<Lead>) =>
      patch<{ lead: Lead }>(`/api/marketing/leads/${id}`, body),
    delete: (id: string) => del<unknown>(`/api/marketing/leads/${id}`),
    listTouchpoints: (id: string) =>
      get<{ touchpoints: LeadTouchpoint[] }>(`/api/marketing/leads/${id}/touchpoints`),
    addTouchpoint: (
      id: string,
      body: {
        type: CreateTouchpointTypeApi
        direction: CreateTouchpointDirectionApi
        summary?: string
        duration_minutes?: number
        recording_url?: string
        outcome?: string
      }
    ) =>
      post<{ id: string; lead_contact_state_advanced_to: string | null }>(
        `/api/marketing/leads/${id}/touchpoints`,
        body
      ),
    addNote: (id: string, content: string) =>
      post<{ id: string }>(`/api/marketing/leads/${id}/notes`, { content }),
  },

  lots: {
    list: (params: { outreach_status?: string; qualifies?: boolean } = {}) => {
      const q = new URLSearchParams()
      if (params.outreach_status) q.set('outreach_status', params.outreach_status)
      if (params.qualifies !== undefined) q.set('qualifies', params.qualifies ? 'true' : 'false')
      const qs = q.toString()
      return get<{ lots: LotMonitoring[] }>(`/api/lots${qs ? `?${qs}` : ''}`).then((d) => d.lots)
    },
    create: (body: Partial<LotMonitoring>) =>
      post<{ lot: LotMonitoring }>('/api/lots', body).then((d) => d.lot),
    update: (id: string, body: Partial<LotMonitoring>) =>
      put<{ lot: LotMonitoring }>(`/api/lots/${id}`, body).then((d) => d.lot),
    delete: (id: string) => del<unknown>(`/api/lots/${id}`),
    draftLetter: (id: string) =>
      post<{ lot: LotMonitoring }>(`/api/lots/${id}/draft-letter`, {}).then((d) => d.lot),
    sendLetter: (id: string, recipient_email: string) =>
      post<{ message_id: string }>(`/api/lots/${id}/send-letter`, { recipient_email }),
  },

  forecast: {
    get: (projectId: string) =>
      get<{
        project: { id: string; name: string }
        health: 'green' | 'yellow' | 'red'
        summary: {
          total_budget: number
          total_drawn: number
          total_remaining: number
          pct_schedule_complete: number
          pct_budget_spent: number
          projected_final_cost: number
          projected_overrun: number
          contingency_total: number
          contingency_remaining: number
          approved_change_orders: number
          pending_change_orders: number
          draws_funded: number
          draws_submitted: number
        }
        benchmarks: {
          project_sqft: number | null
          psf_actual: number | null
          psf_projected: number | null
          east_metro_luxury: { low: number; mid: number; high: number }
        }
        line_forecasts: Array<{
          line_number: number
          item: string
          sub_name: string | null
          budgeted: number
          drawn: number
          pct_drawn: number
          projected_final: number
          projected_variance: number
        }>
        phase_rollup: Array<{
          phase: string
          budgeted: number
          drawn: number
          variance: number
          line_count: number
        }>
        warnings: string[]
      }>(`/api/projects/${projectId}/forecast`),
  },

  photoProgress: {
    detect: (projectId: string, body: { walk_id?: string; prior_walk_id?: string } = {}) =>
      post<{
        older_walk: { id: string; date: string; photo_count: number }
        newer_walk: { id: string; date: string; photo_count: number }
        progress_observations?: string[]
        suggested_task_completions?: Array<{ task_id: string; task_name: string; evidence: string }>
        phase_pct_complete_estimate?: number
        notes?: string
      }>(`/api/projects/${projectId}/progress/detect`, body),
    apply: (projectId: string, taskIds: string[]) =>
      post<{ updated: number }>(`/api/projects/${projectId}/progress/apply`, { task_ids: taskIds }),
  },

  inspectionPrep: {
    get: (inspectionId: string) =>
      get<{
        prep: {
          checklist: Array<{ item: string; source: string; priority: string }>
          recent_walk_flags: Array<{ item: string; severity: string; walk_date: string }>
          generated_at: string
          reminded_at: string | null
        } | null
      }>(`/api/inspections/${inspectionId}/prep`),
    generate: (inspectionId: string) =>
      post<{
        checklist: Array<{ item: string; source: string; priority: string }>
        preexisting_walk_flags_to_address: Array<{ item: string; severity: string; walk_date: string }>
        scheduling_notes: string
      }>(`/api/inspections/${inspectionId}/prep`, {}),
  },

  endOfBuild: {
    generate: (projectId: string, persist = true) =>
      post<{
        summary?: string
        schedule_accuracy?: unknown
        budget_accuracy?: unknown
        sub_recommendations?: Array<{ sub_name: string; verdict: string; rationale: string }>
        spec_book_recommendations?: string[]
        process_improvements?: string[]
        lessons_inserted?: number
        lessons?: Array<{
          category: string
          trade: string | null
          phase: string | null
          title: string
          body: string
        }>
      }>(`/api/projects/${projectId}/end-of-build/generate`, { persist }),
  },

  weather: {
    geocode: (projectId: string) =>
      post<{
        latitude: number
        longitude: number
        matched_address: string
        grid: { id: string; x: number; y: number }
      }>(`/api/projects/${projectId}/weather/geocode`, {}),
    setCoords: (projectId: string, latitude: number, longitude: number) =>
      post<{ grid: { gridId: string; gridX: number; gridY: number } }>(
        `/api/projects/${projectId}/weather/coords`,
        { latitude, longitude }
      ),
    pull: (projectId: string) =>
      post<{
        forecast: Array<{
          date: string
          high_temp_f: number | null
          low_temp_f: number | null
          precipitation_chance: number | null
          precipitation_type: string | null
          wind_speed_mph: number | null
          short_forecast: string
          detailed_forecast: string
        }>
        conflicts: Array<{
          task_id: string
          task_name: string
          phase: string
          forecast_date: string
          kind: string
          severity: 'info' | 'warning' | 'critical'
          message: string
        }>
        inserted: number
      }>(`/api/projects/${projectId}/weather/pull`, {}),
    get: (projectId: string) =>
      get<{
        forecast: Array<{
          forecast_date: string
          high_temp_f: number | null
          low_temp_f: number | null
          precipitation_chance: number | null
          precipitation_type: string | null
          wind_speed_mph: number | null
          short_forecast: string | null
          detailed_forecast: string | null
        }>
        conflicts: Array<{
          task_id: string
          task_name: string
          phase: string
          forecast_date: string
          kind: string
          severity: 'info' | 'warning' | 'critical'
          message: string
        }>
      }>(`/api/projects/${projectId}/weather`),
  },

  subAgreements: {
    list: (projectId: string) =>
      get<{ agreements: SubAgreement[] }>(`/api/projects/${projectId}/sub-agreements`).then(
        (d) => d.agreements
      ),
    update: (id: string, body: Partial<SubAgreement>) =>
      put<{ agreement: SubAgreement }>(`/api/sub-agreements/${id}`, body).then((d) => d.agreement),
    sendForSignature: (id: string) =>
      post<{ message_id: string }>(`/api/sub-agreements/${id}/send`, {}),
    // SOW Exhibit A — Master Scope-driven scope addendum
    generateExhibitA: (id: string) =>
      post<{
        agreement_id: string
        sub_name: string
        trade: string
        contract_amount: number | null
        exhibit_a_markdown: string
        coverage: {
          expected_scope_items: number
          covered_scope_items: number
          missing_scope_items: Array<{ id: string; scope_item: string; spec_reference: string | null }>
          coverage_pct: number
        }
        spec_subsections_count: number
        plan_sheets_count: number
        warnings: string[]
      }>(`/api/sub-agreements/${id}/exhibit-a`, {}),
    getExhibitA: (id: string) =>
      get<{
        ok: true
        exhibit_a: string | null
        exhibit_a_generated_at: string | null
        exhibit_a_coverage_pct: number | null
      }>(`/api/sub-agreements/${id}/exhibit-a`),
  },

  sowCoverage: {
    run: (projectId: string) =>
      post<{
        project_id: string
        total_scope_items: number
        total_scs_lines: number
        total_sub_projects: number
        total_sub_agreements: number
        orphan_scope_items: Array<{ id: string; category: string; scope_item: string }>
        uncovered_trades: Array<{ trade: string; scope_count: number; reason: string }>
        untracked_scs_lines: Array<{ line_number: number; item: string; sub_name: string | null }>
        overlap_candidates: Array<{ scope_item: string; trades: string[] }>
        idle_sub_projects: Array<{ sub_name: string; role: string | null }>
        coverage_pct: number
        gaps_inserted: number
      }>(`/api/projects/${projectId}/sow-coverage/run`, {}),
    get: (projectId: string) =>
      get<{
        project_id: string
        total_scope_items: number
        total_scs_lines: number
        total_sub_projects: number
        total_sub_agreements: number
        orphan_scope_items: Array<{ id: string; category: string; scope_item: string }>
        uncovered_trades: Array<{ trade: string; scope_count: number; reason: string }>
        untracked_scs_lines: Array<{ line_number: number; item: string; sub_name: string | null }>
        overlap_candidates: Array<{ scope_item: string; trades: string[] }>
        idle_sub_projects: Array<{ sub_name: string; role: string | null }>
        coverage_pct: number
        gaps_inserted: number
      }>(`/api/projects/${projectId}/sow-coverage`),
  },
}
